import { Component } from "@theme/component";
import {
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
// R57c-2: fetchConfig is gone from here - the controller owns the request now -
// and morphSection was imported but never used.
import { morphCartPortion, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * R-TAP (17 Sep 2026). What this replaced, and why none of it could stay.
   *
   * There used to be ONE `debounce(this.#onQuantityChange, 300)` for the whole
   * component, and `debounce` in utilities.js keeps only the LAST call. Two
   * consequences, both reproduced on the dev theme:
   *
   *   B1 - a tap on line 1 followed by a tap on line 2 inside 300 ms sent ONLY
   *        line 2's change. Line 1 showed 2 on screen and snapped back to 1.
   *        A silently dropped tap, on a different product from the one touched.
   *   B2 - `#disableCartItems()` put `pointer-events: none` over the whole
   *        component for the length of the round trip. Hit-tested on the box
   *        panel `+`: the button at +0 and +100 ms, a background DIV from
   *        +350 ms to +900 ms, the button again at +1,100 ms. Tap, tap,
   *        nothing - which is most of what "the cart feels slow" actually was.
   *
   * The replacement is a queue keyed BY CART LINE, and it never blocks input:
   *
   *   - the FIRST tap leaves immediately. No wait at all, so the 300 ms comes
   *     straight off the front of every interaction.
   *   - taps arriving while a request is out are recorded, per line, and sent
   *     when it lands. Same shape as product-card-alp.js's pendingUpdate /
   *     queuedQuantity, which has held since R57a.
   *   - a second tap on the SAME line overwrites that line's entry, so a burst
   *     of five `+` costs two requests and lands on +5, not +1 and not +3.
   *   - a tap on a DIFFERENT line is its own entry and cannot be overwritten by
   *     anyone else's, which is B1.
   *   - the controls stay live throughout. Nothing is covered, so a tap always
   *     lands on the control the customer aimed at and is absorbed by it - the
   *     R60 failure was a busy control letting a tap fall THROUGH to what sat
   *     behind it, and a live button cannot do that.
   *
   * ONE REQUEST AT A TIME, deliberately, even across different lines. The cart
   * is a single server-side resource and Shopify answers concurrent writes to
   * it with 409, which cart-controller.js classifies as a transport failure:
   * a generic banner and a reconcile. Running two lines in parallel would swap
   * a dropped tap for an error message, so the queue is drained in order.
   *
   * @type {Map<string, {line: number, quantity: number, knownQuantity?: number}>}
   */
  #pending = new Map();

  /** @type {boolean} whether a cart request from this surface is out. */
  #draining = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityChange
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    // Only handle events that originated from within this component's DOM
    // This prevents duplicate handling when multiple cart-items-components exist
    const target = /** @type {Node | null} */ (event.target);
    if (!target || !this.contains(target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    const key = this.#lineKeyFrom(target, line);

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow?.querySelector("text-component")
    );
    textComponent?.shimmer();

    this.#queueQuantity(line, key, quantity, this.#renderedQuantityFor(key, line));
  };

  /**
   * R-STALEKEY. The quantity the SERVER last told us this row holds.
   *
   * `defaultValue` is the `value` ATTRIBUTE, which the morph sets from the cart
   * response, so it is the server's number rather than whatever the customer has
   * since typed or clicked it to. cart-controller's dead-key recovery needs it to
   * tell a line that merely changed key (same quantity) from one that ABSORBED
   * another during a subscription conversion (more), because applying an absolute
   * quantity to the second deletes items nobody touched. Codex round
   * CART-BUNDLING-STALEKEY finding 2.
   *
   * @param {string | null} key
   * @param {number} line - rendered index, DOM lookup fallback only.
   * @returns {number | undefined} undefined when it cannot be read, which makes
   *   the controller refuse to re-address rather than guess.
   */
  #renderedQuantityFor(key, line) {
    const row = key
      ? this.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`)
      : null;

    const input = /** @type {HTMLInputElement | null} */ (
      row?.querySelector("input[data-cart-line]") ??
        this.refs.quantitySelectors?.[line - 1]?.querySelector("input") ??
        null
    );

    const parsed = Number(input?.defaultValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  /**
   * Sends a quantity change for one line, or folds it into the change already
   * in flight for that same line.
   *
   * @param {number} line - The rendered line index, for DOM lookups only.
   * @param {string | null} key - The Shopify cart line key.
   * @param {number} quantity - The quantity the customer has just asked for.
   * @param {number} [knownQuantity] - R-STALEKEY: what the server last said this
   *   row holds, captured at tap time and passed through to cart-controller so a
   *   dead key can be re-addressed without acting on a merged line.
   */
  #queueQuantity(line, key, quantity, knownQuantity) {
    // No key is a hard failure, not something to queue: updateQuantity owns
    // that message and the reconcile that follows it.
    if (!key) {
      this.updateQuantity({
        line,
        key,
        quantity,
        action: quantity === 0 ? "clear" : "change",
      });
      return;
    }

    // A second tap on the same line overwrites the entry, but `knownQuantity` is
    // a fact about the SERVER's last word on this row, not about the tap, so the
    // first reading is kept - the row has not been re-rendered in between.
    const existing = this.#pending.get(key);
    this.#pending.set(key, {
      line,
      quantity,
      knownQuantity: existing?.knownQuantity ?? knownQuantity,
    });
    this.#drain();
  }

  /**
   * Sends every outstanding line change, one at a time, until nothing is left.
   * Re-entrant by design: a tap that arrives mid-drain just adds to the map and
   * the loop picks it up on its next turn.
   */
  async #drain() {
    if (this.#draining) return;
    this.#draining = true;

    try {
      while (this.#pending.size > 0) {
        const [key, next] = /** @type {[string, {line: number, quantity: number, knownQuantity?: number}]} */ (
          this.#pending.entries().next().value
        );
        this.#pending.delete(key);

        await this.updateQuantity({
          line: next.line,
          key,
          quantity: next.quantity,
          knownQuantity: next.knownQuantity,
          action: next.quantity === 0 ? "clear" : "change",
        });
      }
    } finally {
      this.#draining = false;
    }
  }

  /**
   * The number this line is on its way to, if one is still queued. Used after a
   * morph so an intermediate server response cannot snap the stepper back to a
   * figure the customer has already moved past.
   * @param {string} key
   * @returns {number | null}
   */
  #pendingQuantityFor(key) {
    return this.#pending.get(key)?.quantity ?? null;
  }

  /**
   * Resolves the Shopify cart line key for an interaction.
   *
   * R57c-2: R57a established that cart lines must be addressed by the key
   * Shopify hands us, never by position in a list - positions shuffle every time
   * another row goes away. This path was the last one still sending `line`. The
   * key is read from the row the interaction actually happened in, walking up
   * from the event target, rather than from `refs.cartItemRows[line - 1]`: that
   * array goes stale the instant a row is optimistically removed, which is
   * precisely the window in which a customer clicks the next control.
   * @param {EventTarget | null} node - Where the interaction came from.
   * @param {number} line - The rendered line index, used only as a fallback.
   * @returns {string | null}
   */
  #lineKeyFrom(node, line) {
    if (node instanceof Element) {
      const row = node.closest("[data-cart-line-key]");
      if (row) return row.getAttribute("data-cart-line-key");
    }

    return this.refs.cartItemRows?.[line - 1]?.getAttribute("data-cart-line-key") ?? null;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The originating event. `on:click="/onLineItemRemove/N"`
   *   passes the proxied event as the SECOND argument (assets/component.js
   *   registerEventListeners), so the markup needs no change to supply it.
   */
  onLineItemRemove(line, event) {
    const key = this.#lineKeyFrom(event?.target ?? null, line);

    // R-TAP: a removal goes through the same queue as everything else. With
    // nothing outstanding that is indistinguishable from the direct call it
    // replaced - the request leaves in the same task. With a quantity change
    // still out on this line it stops the two racing each other, where the
    // later response wins the morph and can put a row the customer has just
    // deleted back on screen. The optimistic row removal below is unchanged and
    // still happens immediately.
    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];

    // R-RAGE: a SECOND tap on a Remove that is already on its way out used to send a
    // second /cart/change for a line key the server no longer has. Shopify answers
    // "no valid id or line parameter" and that raw API string was shown to the
    // customer in the R89 banner - for a removal that had in fact SUCCEEDED. Worst
    // kind of error message: the thing you asked for worked and the page says it did
    // not. Reproduced on LIVE `147519504499` (two requests, 4 ms and 204 ms, one
    // `cart:error`, cart correct) and on dev, where the R-TAP queue serialises the
    // two but does not stop the second being sent.
    // The row carries `removing` from the moment the first tap lands and is animating
    // away over ~500 ms, which is exactly the window a rage tap falls in. It has
    // nothing left to ask for, so absorb the tap rather than ask the server again.
    // Deliberately NOT `pointer-events: none` on the row: R60 says a busy control
    // must absorb a tap, never let it fall through to whatever is behind it.
    if (cartItemRowToRemove?.classList.contains("removing")) return;

    this.#queueQuantity(line, key, 0, this.#renderedQuantityFor(key, line));

    if (!cartItemRowToRemove) return;

    // R57c-2: the row is still removed optimistically, but a morph may have
    // reused this node for a different line by the time the animation ends, and
    // a failed request now triggers a reconcile that re-renders the truth. Only
    // remove the node if it is still attached AND still the line we meant.
    const remove = () => {
      if (!cartItemRowToRemove.isConnected) return;
      if (key && cartItemRowToRemove.getAttribute("data-cart-line-key") !== key) return;
      cartItemRowToRemove.remove();
    };

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`
    );
    cartItemRowToRemove.classList.add("removing");

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity of one cart line.
   *
   * R57c-2: the controller owns the round trip. It builds the body from the
   * registry, fetches, parses, dispatches the single CartUpdateEvent that drives
   * every surface's morph and the single store update that follows, and re-reads
   * cart state from the server when the response cannot be trusted - a 429 with
   * an HTML body, or a bundled-sections response carrying `null`. That last part
   * is why this path matters most: the drawer deletes a line row before the
   * server has answered, so a failure used to leave the drawer showing a cart
   * that no longer matched reality.
   *
   * The explicit `morphCartPortion` that used to follow the dispatch is gone.
   * The controller dispatches on `document`, so `event.target === this` is false
   * and `#handleCartUpdate` morphs this surface like any other - doing it twice
   * repeated the same DOM work.
   *
   * @param {Object} config - The config.
   * @param {number} config.line - The rendered line index, for DOM lookups only.
   * @param {string | null} config.key - The Shopify cart line key, for the server.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    // R-TAP: no `#disableCartItems()` here any more. See the note on `#pending`.
    const { line, key, quantity, knownQuantity } = config;
    const { cartTotal, cartTotalOriginal } = this.refs;

    cartTotal?.shimmer();
    cartTotalOriginal?.shimmer();

    try {
      // R57c: raise, never return in silence. A stepper the customer can still
      // press but that cannot address its own cart line is the exact failure
      // shape R57a left behind, and it reads as "the button did nothing".
      if (!key) {
        // R90b, Codex round 35 finding 9. Reconciling re-reads the server and
        // snaps the stepper back, so the customer sees the number they just set
        // silently revert. That reads as the control fighting them, not as an
        // error. Say what happened.
        const error = new Error(
          `no cart line key for rendered line ${line}; this change was not sent`
        );
        console.error("[cart-items-component]", error.message, "- re-reading cart state");
        await window.cartController?.reconcile?.("cart-items-component");
        window.ctrNotifyCartFailure?.(
          "Sorry, your basket could not be updated. Please try again.",
          "cart-items-component",
          error
        );
        return;
      }

      if (!window.cartController?.change) {
        const error = new Error("cart-controller.js is not loaded; no cart request was sent");
        console.error("[cart-items-component]", error.message);
        window.ctrNotifyCartFailure?.(
          "Sorry, your basket could not be updated. Please refresh the page and try again.",
          "cart-items-component",
          error
        );
        return;
      }

      const result = await window.cartController.change(
        { id: key, quantity },
        { source: "cart-items-component", knownQuantity }
      );

      // R-TAP, Codex round TAP finding 2. THE QUEUE MUST BE DROPPED ON A REFUSAL.
      //
      // Five rapid `+` past available stock used to be one request under the old
      // 300 ms debounce and therefore one message. Coalescing sends two - the
      // first target, then the one that arrived while it was out - and if the
      // FIRST is already over stock both are refused independently, so the
      // customer is told the same thing twice. Shopify's answer will not change
      // between them: a bigger number is refused for the same reason.
      //
      // Same for a transport failure. A 429 replayed immediately earns another,
      // which is the loop RECONCILE_BACKOFF_MS exists to avoid.
      //
      // Codex round TAP-3 finding 1: THE RESET IS UNCONDITIONAL ON FAILURE NOW.
      // It used to be gated on `result.data?.errors`, and with a queued follow-up
      // that left the optimistic number standing. The order of events is the
      // trap: cartController reconciles from inside change(), which dispatches
      // the cart update, which morphs the server's quantity in and then runs
      // #reapplyPendingQuantities() - writing the QUEUED target over the truth
      // that had just arrived. Only then does this code run. A 429 comes back
      // with `data === null` and a 422 may carry only `status`/`description`, so
      // neither had an `errors` key and neither snapped back: one message, and a
      // number that disagreed with the cart until a reload.
      //
      // R90b, Codex round 35 finding 10. cartController.change() has already
      // surfaced this rejection globally before returning. This branch used to
      // paint the same text again against the line, so one refused change
      // produced two messages.
      //
      // What is kept is the part that was never a message: putting the stepper
      // back to the quantity the cart actually holds. `defaultValue` is the
      // `value` ATTRIBUTE, which the morph has just set from the server, so this
      // is the truth rather than a remembered number.
      if (!result.ok) {
        if (key) this.#pending.delete(key);
        this.#resetLineQuantity(line, key);
      }
    } catch (error) {
      // An exception here means the change is indeterminate. Treat it as failed.
      console.error("[cart-items-component] cart change threw unexpectedly", error);
      window.ctrNotifyCartFailure?.(
        "Sorry, your basket could not be updated. Please try again.",
        "cart-items-component",
        error
      );
    } finally {
      // Only clear the shimmer once the queue is empty. With a second line
      // still waiting to be sent, the first one home used to strip its shimmer
      // and the surface looked settled while a change was still outstanding.
      if (this.#pending.size === 0) resetShimmer(this);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * R90b: state correction only. This used to be `#handleCartError`, which also
   * rendered the rejection text against the line - the duplicate of the message
   * cart-controller.js had already shown. Putting the stepper back is the part
   * that was never a message and still has to happen.
   *
   * It also no longer throws when a ref is missing. A missing input is a reason
   * to leave the stepper alone, not a reason to convert a handled rejection into
   * an unhandled exception on the way out.
   * @param {number} line - The 1-based rendered line.
   */
  #resetLineQuantity = (line, key) => {
    // R-TAP, Codex round TAP-7 finding 2: ADDRESSED BY KEY, NOT BY POSITION.
    //
    // Same lesson as R57a, arriving by a new route. The queue records the rendered
    // `line` index when the tap happens, and a morph can renumber every row before
    // the request settles - remove an earlier line, tap `+` on a later one while
    // that removal is still out, and by the time a refusal needs rolling back, the
    // index points at a different row or at nothing. The key travels with the
    // queue entry and does not move.
    const row = key
      ? this.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`)
      : null;

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      row?.querySelector("input[data-cart-line]") ??
        this.refs.quantitySelectors?.[line - 1]?.querySelector("input") ??
        null
    );

    if (!quantityInput) {
      console.error("[cart-items-component] no quantity input for line", line, "key", key, "- stepper left as it is");
      return;
    }

    // `defaultValue` is the `value` ATTRIBUTE, which the morph has just set from
    // the server, so this restores the truth rather than a remembered number.
    quantityInput.value = quantityInput.defaultValue;
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphCartPortion(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#reapplyPendingQuantities();
    // R57b: no store dispatch here. With two cart surfaces on the page this
    // handler runs once per surface, so dispatching from inside it meant two
    // or three store updates per mutation. The initiator of the mutation
    // dispatches cart-drawer-store:update exactly once instead.
  };

  /**
   * Puts the number the customer is actually heading for back into the stepper
   * after a morph.
   *
   * Without this, a burst of taps flickers. Five `+` taps send 2 straight away
   * and coalesce the rest; the response for 2 morphs the row and the stepper
   * drops from 6 to 2 for as long as the replay takes, then jumps to 6. The
   * server response is correct at the moment it was made and stale by the time
   * it arrives, so the outstanding target wins.
   */
  #reapplyPendingQuantities() {
    if (this.#pending.size === 0) return;

    // Queried off the DOM rather than `refs.cartItemRows`: refs are refreshed
    // by a MutationObserver callback, which has not run yet at the moment the
    // morph returns, so the ref array can still describe the pre-morph rows.
    for (const row of this.querySelectorAll("[data-cart-line-key]")) {
      const key = row.getAttribute("data-cart-line-key");
      if (!key) continue;

      const pending = this.#pendingQuantityFor(key);
      if (pending == null) continue;

      // R-TAP, Codex round TAP finding 1. A REMOVE QUEUED BEHIND A QUANTITY
      // CHANGE ON THE SAME LINE CAME BACK ON SCREEN. The row was taken out
      // optimistically when the customer pressed the cross; the earlier
      // change's response then morphed the server's copy of it back in, and
      // setting its input to 0 left a visible row reading 0 until the removal
      // landed. Take it out again. If the removal then fails, the controller
      // reconciles and re-renders the truth, which is the same guarantee the
      // optimistic removal has always rested on.
      if (pending === 0) {
        row.remove();
        continue;
      }

      const input = /** @type {HTMLInputElement | null} */ (
        row.querySelector("input[data-cart-line]")
      );
      if (input) input.value = String(pending);
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
